"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import AnimatedGrid from "@/components/AnimatedGrid";
import { useTranslation } from "react-i18next";
import ContactSalesModal from "@/components/ContactSalesModal";
import { Link } from "react-router-dom";

const HeroSection = () => {
  const { t } = useTranslation();
  const [salesModalOpen, setSalesModalOpen] = useState(false);

  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      <ContactSalesModal open={salesModalOpen} onOpenChange={setSalesModalOpen} />
      <AnimatedGrid />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-secondary/60 to-transparent" />

      <div className="container mx-auto px-4 md:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-muted-foreground text-xs font-medium mb-6 border border-border"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-trust" />
              {String(t("hero.badge"))}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="font-display text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground leading-[1.15] tracking-tight text-balance mb-6"
            >
              {String(t("hero.titlePart1"))}{" "}
              <span className="text-accent">{String(t("hero.titleHighlight"))}</span>{" "}
              {String(t("hero.titlePart2"))}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-lg"
            >
              {String(t("hero.description"))}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="flex flex-col sm:flex-row gap-4 items-start sm:items-center"
            >
              <Button
                size="lg"
                className="gap-2 text-sm font-semibold px-6"
                onClick={() => setSalesModalOpen(true)}
              >
                {String(t("hero.ctaPrimary"))}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button asChild variant="outline" size="lg" className="gap-2 text-sm font-semibold px-6">
                <Link to="/sovereign-continuity">
                  {String(t("hero.ctaSecondary"))}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="hidden lg:flex items-center justify-center relative"
          >
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/10 via-accent/10 to-transparent blur-3xl scale-110" />
            <BrandLogo
              variant="mark"
              className="relative"
              imgClassName="h-auto w-full max-w-xs drop-shadow-lg"
              alt="Resilience365"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
